import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, CircularProgress, Alert, Tooltip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import API from '../api';

// --- AES-256-GCM crypto helpers ---

// PBKDF2 iterations: legacy = blobs створені до 2026-05-18 (без поля `iter`);
// current = OWASP 2023+ стандарт для нових/перешифрованих секретів.
const PBKDF2_ITERATIONS_LEGACY = 200000;
const PBKDF2_ITERATIONS_CURRENT = 600000;

async function deriveKey(password, saltBytes, iterations) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBytes, iterations, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function bufToB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function b64ToBuf(s) {
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

async function encryptValue(password, plaintext) {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const iterations = PBKDF2_ITERATIONS_CURRENT;
  const key = await deriveKey(password, salt, iterations);
  const enc = new TextEncoder();
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext));
  return JSON.stringify({ salt: bufToB64(salt), iv: bufToB64(iv), ct: bufToB64(ct), iter: iterations });
}

async function decryptValue(password, encryptedJson) {
  const { salt, iv, ct, iter } = JSON.parse(encryptedJson);
  const iterations = typeof iter === 'number' ? iter : PBKDF2_ITERATIONS_LEGACY;
  const key = await deriveKey(password, b64ToBuf(salt), iterations);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64ToBuf(iv) }, key, b64ToBuf(ct));
  return new TextDecoder().decode(plain);
}

// --- Master password dialog ---

function MasterPasswordDialog({ open, onClose, onConfirm, title }) {
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => { setPwd(''); setError(''); onClose(); };

  const handleConfirm = () => {
    if (!pwd.trim()) { setError('Пароль не може бути порожнім'); return; }
    const p = pwd;
    setPwd(''); setError('');
    onConfirm(p);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: '#fff' }}>{title || 'Мастер-пароль'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth type="password" label="Мастер-пароль"
          value={pwd} onChange={e => setPwd(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleConfirm()}
          error={!!error}
          helperText={error || 'Пароль ніколи не залишає ваш браузер'}
          sx={{ mt: 1 }}
          InputProps={{ style: { color: '#fff' } }}
          InputLabelProps={{ style: { color: '#aaa' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ color: '#aaa' }}>Скасувати</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">OK</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Change master password dialog (Feat #11) ---

function ChangeMasterPasswordDialog({ open, onClose, onChanged }) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const reset = () => {
    setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    setShowOld(false); setShowNew(false);
    setProgress(null); setError(''); setDone(null);
  };

  const isRunning = progress && progress.stage !== 'done';

  const handleClose = () => {
    if (isRunning) return;
    reset(); onClose();
  };

  const validate = () => {
    if (!oldPwd) return 'Введіть поточний мастер-пароль';
    if (!newPwd) return 'Введіть новий мастер-пароль';
    if (newPwd.length < 12) return 'Мінімум 12 символів';
    if (newPwd === oldPwd) return 'Новий пароль має відрізнятись від поточного';
    if (newPwd !== confirmPwd) return 'Новий пароль і підтвердження не збігаються';
    return null;
  };

  const handleChange = async () => {
    const ve = validate();
    if (ve) { setError(ve); return; }
    setError('');

    try {
      setProgress({ stage: 'list' });
      const listResp = await API.get('/secrets');
      const withValue = listResp.data.filter(s => s.has_value);

      if (withValue.length === 0) {
        setError('Жодного заповненого секрету — нічого перешифровувати');
        setProgress(null);
        return;
      }

      // Pre-flight: round-trip self-test з новим паролем
      const testBlob = await encryptValue(newPwd, 'pre-flight-check');
      const testDecrypt = await decryptValue(newPwd, testBlob);
      if (testDecrypt !== 'pre-flight-check') {
        throw new Error('Self-test шифрування не пройшов — Web Crypto API проблема');
      }

      const newBlobs = [];
      for (let i = 0; i < withValue.length; i++) {
        const s = withValue[i];
        setProgress({ stage: 'reencrypt', total: withValue.length, done: i, current: s.name });

        const encResp = await API.get('/secrets/' + s.id + '/encrypted');
        const oldBlob = encResp.data.encrypted_value;
        if (!oldBlob) continue;

        let plaintext;
        try {
          plaintext = await decryptValue(oldPwd, oldBlob);
        } catch (e) {
          throw new Error('Не вдалось розшифрувати "' + s.name + '" — невірний поточний пароль?');
        }

        const newBlob = await encryptValue(newPwd, plaintext);
        newBlobs.push({ id: s.id, encrypted_value: newBlob });
      }

      setProgress({ stage: 'save', total: newBlobs.length });
      await API.put('/secrets/bulk-reencrypt', { secrets: newBlobs });

      setProgress({ stage: 'done', total: newBlobs.length });
      setDone(newBlobs.length);
      if (onChanged) onChanged(newPwd);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка');
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #333' } }}>
      <DialogTitle sx={{ color: '#fff' }}>🔑 Змінити мастер-пароль</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {!done && (
          <Alert severity="warning" sx={{ bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800', '& .MuiAlert-icon': { color: '#ff9800' } }}>
            Усі секрети будуть перешифровані новим паролем атомарно. Перед стартом збережено dump у /var/backups/secrets/.
          </Alert>
        )}
        {done && (
          <Alert severity="success" sx={{ bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50', '& .MuiAlert-icon': { color: '#4caf50' } }}>
            Готово — {done} секретів перешифровано. <strong>Запиши новий пароль зараз</strong> у безпечне місце.
          </Alert>
        )}
        {!done && (
          <>
            <TextField
              label="Поточний мастер-пароль" type={showOld ? 'text' : 'password'}
              value={oldPwd} onChange={e => setOldPwd(e.target.value)} fullWidth disabled={isRunning}
              InputProps={{ style: { color: '#fff' }, endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowOld(!showOld)} edge="end" sx={{ color: '#aaa' }} size="small">
                    {showOld ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ) }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label="Новий мастер-пароль (≥12 символів)" type={showNew ? 'text' : 'password'}
              value={newPwd} onChange={e => setNewPwd(e.target.value)} fullWidth disabled={isRunning}
              InputProps={{ style: { color: '#fff' }, endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew(!showNew)} edge="end" sx={{ color: '#aaa' }} size="small">
                    {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ) }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label="Підтвердження нового" type={showNew ? 'text' : 'password'}
              value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} fullWidth disabled={isRunning}
              InputProps={{ style: { color: '#fff' } }}
              InputLabelProps={{ style: { color: '#aaa' } }}
            />
          </>
        )}
        {error && <Typography variant="body2" sx={{ color: '#f44336' }}>{error}</Typography>}
        {progress && progress.stage !== 'done' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} sx={{ color: 'var(--ac1, #7c4dff)' }} />
            <Typography variant="body2" sx={{ color: '#9c7cff' }}>
              {progress.stage === 'list' && 'Завантаження списку...'}
              {progress.stage === 'reencrypt' && 'Перешифровка ' + progress.done + '/' + progress.total + (progress.current ? ' — "' + progress.current + '"' : '')}
              {progress.stage === 'save' && 'Збереження ' + progress.total + ' атомарно...'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!done && (
          <Button onClick={handleClose} disabled={isRunning} sx={{ color: '#aaa' }}>Скасувати</Button>
        )}
        {!done && (
          <Button onClick={handleChange} variant="contained" disabled={isRunning} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>
            {isRunning ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Змінити'}
          </Button>
        )}
        {done && (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: '#4caf50' }}>Закрити</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// --- Add secret dialog ---

function AddSecretDialog({ open, onClose, onAdded, masterPassword, onNeedPassword }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingPwd, setPendingPwd] = useState(false);

  const reset = () => { setName(''); setCategory(''); setValue(''); setIsPublic(false); setError(''); };

  const doAdd = async (pwd) => {
    setPendingPwd(false);
    if (!name.trim()) { setError('Назва обовʼязкова'); return; }
    setLoading(true);
    setError('');
    try {
      const resp = await API.post('/secrets', {
        name: name.trim(),
        category: category.trim() || null,
        category_emoji: null,
        is_public: isPublic,
      });
      const newId = resp.data.id;

      if (value.trim() && pwd) {
        const encBlob = await encryptValue(pwd, value.trim());
        await API.put('/secrets/' + newId, { encrypted_value: encBlob });
      }

      reset();
      onClose();
      onAdded();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) { setError('Назва обовʼязкова'); return; }
    if (value.trim()) {
      if (masterPassword) {
        doAdd(masterPassword);
      } else {
        setPendingPwd(true);
      }
    } else {
      doAdd(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #333' } }}>
        <DialogTitle sx={{ color: '#fff' }}>Додати секрет</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Назва" value={name} onChange={e => setName(e.target.value)}
            fullWidth required error={!!error} helperText={error}
            InputProps={{ style: { color: '#fff' } }} InputLabelProps={{ style: { color: '#aaa' } }}
          />
          <TextField
            label="Категорія" value={category} onChange={e => setCategory(e.target.value)}
            fullWidth placeholder="telegram, server, api, database..."
            InputProps={{ style: { color: '#fff' } }} InputLabelProps={{ style: { color: '#aaa' } }}
          />
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />}
            label={<Typography sx={{ color: '#ddd' }}>{isPublic ? '🌐 Публічний' : '🔒 Приватний'}</Typography>}
          />
          <TextField
            label="Значення (секрет)" value={value} onChange={e => setValue(e.target.value)}
            fullWidth multiline rows={3}
            helperText="Шифрується AES-256-GCM у браузері. На сервер іде лише зашифрований blob."
            InputProps={{ style: { color: '#fff', fontFamily: 'monospace' } }}
            InputLabelProps={{ style: { color: '#aaa' } }}
            FormHelperTextProps={{ style: { color: '#666' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { reset(); onClose(); }} sx={{ color: '#aaa' }}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Додати'}
          </Button>
        </DialogActions>
      </Dialog>
      <MasterPasswordDialog
        open={pendingPwd}
        onClose={() => setPendingPwd(false)}
        onConfirm={(pwd) => { if (onNeedPassword) onNeedPassword(pwd); doAdd(pwd); }}
        title="Мастер-пароль для шифрування"
      />
    </>
  );
}

// --- Edit secret dialog (metadata + value) ---

function EditMetaDialog({ open, secret, onClose, onSaved, masterPassword, onNeedPassword }) {
  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [emoji, setEmoji] = React.useState('');
  const [isPublic, setIsPublic] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pendingPwd, setPendingPwd] = React.useState(false);

  React.useEffect(() => {
    if (secret) {
      setName(secret.name || '');
      setCategory(secret.category || '');
      setEmoji(secret.category_emoji || '');
      setIsPublic(!!secret.is_public);
      setValue('');
      setError('');
    }
  }, [secret]);

  // pwd != null коли вводимо/міняємо значення; null — лише метадані
  const doSave = async (pwd) => {
    setPendingPwd(false);
    if (!name.trim()) { setError('Назва обовʼязкова'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        name: name.trim(),
        category: category.trim() || null,
        category_emoji: emoji.trim() || null,
        is_public: isPublic,
      };
      if (value.trim() && pwd) {
        payload.encrypted_value = await encryptValue(pwd, value.trim());
      }
      await API.put('/secrets/' + secret.id, payload);
      if (value.trim() && pwd && onNeedPassword) onNeedPassword(pwd);
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) { setError('Назва обовʼязкова'); return; }
    if (value.trim()) {
      if (masterPassword) doSave(masterPassword);
      else setPendingPwd(true);
    } else {
      doSave(null);
    }
  };

  if (!secret) return null;
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #333' } }}>
        <DialogTitle sx={{ color: '#fff' }}>✏️ Редагувати: {secret.name}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Назва" value={name} onChange={e => setName(e.target.value)}
            fullWidth required error={!!error} helperText={error}
            InputProps={{ style: { color: '#fff' } }} InputLabelProps={{ style: { color: '#aaa' } }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Категорія" value={category} onChange={e => setCategory(e.target.value)}
              sx={{ flex: 1 }} placeholder="telegram, server, api..."
              InputProps={{ style: { color: '#fff' } }} InputLabelProps={{ style: { color: '#aaa' } }}
            />
            <TextField
              label="Emoji" value={emoji} onChange={e => setEmoji(e.target.value)}
              sx={{ width: 90 }} placeholder="🔑"
              InputProps={{ style: { color: '#fff', fontSize: 20 } }} InputLabelProps={{ style: { color: '#aaa' } }}
            />
          </Box>
          <FormControlLabel
            control={<Switch checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />}
            label={<Typography sx={{ color: '#ddd' }}>{isPublic ? '🌐 Публічний' : '🔒 Приватний'}</Typography>}
          />
          <TextField
            label={secret.has_value ? 'Нове значення (замінить поточне)' : 'Значення (секрет)'}
            value={value} onChange={e => setValue(e.target.value)}
            fullWidth multiline rows={3}
            helperText={secret.has_value
              ? 'Залиш порожнім, щоб зберегти поточне значення. Введене — перезапише його (AES-256-GCM у браузері).'
              : 'Шифрується AES-256-GCM у браузері. На сервер іде лише зашифрований blob.'}
            InputProps={{ style: { color: '#fff', fontFamily: 'monospace' } }}
            InputLabelProps={{ style: { color: '#aaa' } }}
            FormHelperTextProps={{ style: { color: '#666' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: '#aaa' }}>Скасувати</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}
            sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3de8' } }}>
            {loading ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
      <MasterPasswordDialog
        open={pendingPwd}
        onClose={() => setPendingPwd(false)}
        onConfirm={(pwd) => doSave(pwd)}
        title="Мастер-пароль для шифрування"
      />
    </>
  );
}

// --- Secret row ---

function SecretRow({ secret, masterPassword, onSetMasterPassword, onRefresh }) {
  const [revealed, setRevealed] = useState(false);
  const [plaintext, setPlaintext] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const doReveal = async (pwd) => {
    setShowPwd(false);
    if (!secret.has_value) { setPlaintext('(порожньо)'); setRevealed(true); return; }
    setLoading(true);
    setError('');
    try {
      const resp = await API.get('/secrets/' + secret.id + '/encrypted');
      const blob = resp.data.encrypted_value;
      if (!blob) { setPlaintext('(порожньо)'); setRevealed(true); return; }
      const pt = await decryptValue(pwd, blob);
      setPlaintext(pt);
      setRevealed(true);
      if (onSetMasterPassword) onSetMasterPassword(pwd);
    } catch (e) {
      if (e.name === 'OperationError') setError('Невірний пароль');
      else setError(e?.response?.data?.detail || e.message || 'Помилка');
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = () => {
    if (masterPassword) doReveal(masterPassword);
    else setShowPwd(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(plaintext);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!window.confirm('Видалити "' + secret.name + '"?')) return;
    try {
      await API.delete('/secrets/' + secret.id);
      onRefresh();
    } catch (e) {
      alert('Помилка: ' + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <>
      <TableRow hover sx={{ '&:hover': { bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.05)' } }}>
        <TableCell sx={{ color: '#fff', fontWeight: 500 }}>{secret.name}</TableCell>
        <TableCell>
          {secret.category
            ? <Chip label={`${secret.category_emoji || ''} ${secret.category}`.trim()} size="small" variant="outlined" sx={{ color: '#aaa', borderColor: '#555', fontSize: 11 }} />
            : <Typography variant="caption" sx={{ color: '#555' }}>—</Typography>}
        </TableCell>
        <TableCell>
          <Chip
            label={secret.is_public ? '🌐 Public' : '🔒 Private'}
            size="small"
            sx={{
              bgcolor: secret.is_public ? 'rgba(33,150,243,0.15)' : 'rgba(var(--ac1rgb, 124, 77, 255),0.15)',
              color: secret.is_public ? '#2196f3' : 'var(--ac1, #7c4dff)',
              fontSize: 11
            }}
          />
        </TableCell>
        <TableCell>
          {!secret.has_value && (
            <Chip label="⚠ Немає значення" size="small"
              sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ff9800', fontSize: 11 }} />
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: 320 }}>
          {revealed ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#4caf50', wordBreak: 'break-all', flex: 1, fontSize: 12 }}>
                {plaintext}
              </Typography>
              <Tooltip title="Копіювати">
                <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#4caf50' : '#aaa' }}>
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Сховати">
                <IconButton size="small" onClick={() => { setRevealed(false); setPlaintext(''); }} sx={{ color: '#aaa' }}>
                  <VisibilityOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {error && <Typography variant="caption" sx={{ color: '#f44336' }}>{error}</Typography>}
              <Tooltip title="Показати значення">
                <span>
                  <IconButton size="small" onClick={handleReveal} disabled={loading} sx={{ color: 'var(--ac1, #7c4dff)' }}>
                    {loading ? <CircularProgress size={16} /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Редагувати (назва, категорія, значення)">
            <IconButton size="small" onClick={() => setShowEdit(true)} sx={{ color: '#9c7cff', opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Видалити">
            <IconButton size="small" onClick={handleDelete} sx={{ color: '#f44336', opacity: 0.6, '&:hover': { opacity: 1 } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <MasterPasswordDialog
        open={showPwd}
        onClose={() => setShowPwd(false)}
        onConfirm={doReveal}
        title={'Розшифрувати "' + secret.name + '"'}
      />
      <EditMetaDialog
        open={showEdit}
        secret={secret}
        onClose={() => setShowEdit(false)}
        onSaved={() => { setShowEdit(false); onRefresh(); }}
        masterPassword={masterPassword}
        onNeedPassword={onSetMasterPassword}
      />
    </>
  );
}

// --- Main SecretsPage ---

export default function SecretsPage() {
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masterPassword, setMasterPassword] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showChangeMaster, setShowChangeMaster] = useState(false);
  const [search, setSearch] = useState('');

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await API.get('/secrets');
      setSecrets(resp.data);
    } catch (e) {
      console.error('fetchSecrets:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSecrets(); }, [fetchSecrets]);

  const filtered = secrets.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const categories = [...new Set(filtered.map(s => s.category || '').filter(Boolean))].sort();
  const uncategorized = filtered.filter(s => !s.category);

  const catEmoji = {};
  filtered.forEach(s => { if (s.category && s.category_emoji) catEmoji[s.category] = s.category_emoji; });

  const renderGroup = (title, items) => {
    if (!items.length) return null;
    const emoji = catEmoji[title];
    return (
      <Box key={title} sx={{ mb: 3 }}>
        <Typography variant="overline" sx={{ color: '#888', letterSpacing: 1.5, pl: 0.5, fontSize: 13 }}>
          {emoji ? `${emoji} ${title}` : title}
        </Typography>
        <TableContainer component={Paper} variant="outlined"
          sx={{ bgcolor: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 2, mt: 0.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { color: '#666', fontSize: 11, borderColor: '#2a2a4a', py: 0.8 } }}>
                <TableCell>НАЗВА</TableCell>
                <TableCell>КАТЕГОРІЯ</TableCell>
                <TableCell>ТИП</TableCell>
                <TableCell>СТАТУС</TableCell>
                <TableCell>ЗНАЧЕННЯ</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody sx={{ '& td': { borderColor: '#222', color: '#ccc' } }}>
              {items.map(s => (
                <SecretRow
                  key={s.id}
                  secret={s}
                  masterPassword={masterPassword}
                  onSetMasterPassword={setMasterPassword}
                  onRefresh={fetchSecrets}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const totalCount = secrets.length;
  const emptyCount = secrets.filter(s => !s.has_value).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
            🔐 Секрети та ключі
          </Typography>
          <Typography variant="caption" sx={{ color: '#666' }}>
            Всього: {totalCount} &nbsp;|&nbsp;
            {emptyCount > 0 && <span style={{ color: '#ff9800' }}>Без значення: {emptyCount}</span>}
            {emptyCount === 0 && <span style={{ color: '#4caf50' }}>Всі заповнені ✓</span>}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {masterPassword && (
            <Button
              size="small" variant="outlined"
              startIcon={<KeyIcon />}
              onClick={() => setShowChangeMaster(true)}
              sx={{ color: '#9c7cff', borderColor: '#9c7cff', '&:hover': { borderColor: '#bb9bff' } }}
            >
              Змінити мастер
            </Button>
          )}
          {masterPassword && (
            <Button
              size="small" variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setMasterPassword('')}
              sx={{ color: '#ff9800', borderColor: '#ff9800', '&:hover': { borderColor: '#ffa726' } }}
            >
              Заблокувати
            </Button>
          )}
          <Button
            variant="contained" startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={{ bgcolor: 'var(--ac1, #7c4dff)', '&:hover': { bgcolor: '#6a3de8' } }}
          >
            Додати
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <TextField
        fullWidth size="small"
        placeholder="Пошук за назвою або категорією..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#666' }} /></InputAdornment>,
          style: { color: '#fff', backgroundColor: '#1a1a2e' }
        }}
        sx={{ mb: 2, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
      />

      {/* Status alerts */}
      {masterPassword ? (
        <Alert severity="success" icon={<LockOpenIcon />}
          sx={{ mb: 2, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50', '& .MuiAlert-icon': { color: '#4caf50' } }}>
          Мастер-пароль введено — розшифровка активна. Закрийте вкладку щоб очистити.
        </Alert>
      ) : (
        <Alert severity="info" icon={<LockIcon />}
          sx={{ mb: 2, bgcolor: 'rgba(var(--ac1rgb, 124, 77, 255),0.08)', color: '#9c7cff', '& .MuiAlert-icon': { color: '#9c7cff' } }}>
          <strong>AES-256-GCM шифрування.</strong> Мастер-пароль та значення ніколи не передаються на сервер — лише зашифрований blob.
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }} />
        </Box>
      ) : (
        <>
          {categories.map(cat => renderGroup(cat, filtered.filter(s => s.category === cat)))}
          {renderGroup('Без категорії', uncategorized)}
          {filtered.length === 0 && (
            <Typography sx={{ color: '#555', textAlign: 'center', py: 6 }}>
              {search ? 'Нічого не знайдено' : 'Секретів ще немає'}
            </Typography>
          )}
        </>
      )}

      {/* Add dialog */}
      <AddSecretDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onAdded={fetchSecrets}
        masterPassword={masterPassword}
        onNeedPassword={setMasterPassword}
      />

      {/* Change master password dialog */}
      <ChangeMasterPasswordDialog
        open={showChangeMaster}
        onClose={() => setShowChangeMaster(false)}
        onChanged={(newPwd) => { setMasterPassword(newPwd); fetchSecrets(); }}
      />
    </Box>
  );
}
